import { Shield, FileText, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import logo from "@/assets/logo.png";
import SocialShare from "@/components/SocialShare";

const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20 pb-12">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl" />
      </div>

      <div className="container relative z-10">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center">
          {/* Left content */}
          <div
            className="text-center lg:text-left animate-fade-in-up"
          >
            {/* Badge */}
            <div
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary-muted border border-primary/20 text-primary text-sm font-medium mb-6 animate-scale-in"
            >
              <Shield className="w-4 h-4" />
              Built for tenants in the Netherlands
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground leading-tight mb-6 text-balance">
              Understand your Dutch rental contract{" "}
              <span className="text-primary">before you sign</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground mb-8 max-w-xl mx-auto lg:mx-0 text-balance">
              RentSense NL identifies risky clauses in minutes and explains them
              in plain English. No legal jargon, no subscriptions, just clarity
              when you need it most. We use automated contract analysis based on common Dutch tenancy practices and dispute patterns.
            </p>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button variant="hero" size="xl" asChild>
                <Link to="/app">
                  <FileText className="w-5 h-5" />
                  Upload Your Contract
                </Link>
              </Button>
              <Button variant="hero-outline" size="xl" asChild>
                <a href="#how-it-works">
                  See How It Works
                </a>
              </Button>
            </div>

            {/* Trust indicators */}
            <div
              className="flex flex-wrap items-center gap-6 mt-10 justify-center lg:justify-start animate-fade-in-delayed"
            >
              {[
                "No documents stored",
                "Plain English",
                "One-time payment",
              ].map((item, index) => (
                <div
                  key={index}
                  className="flex items-center gap-2 text-sm text-muted-foreground"
                >
                  <CheckCircle className="w-4 h-4 text-status-ok" />
                  {item}
                </div>
              ))}
            </div>

            {/* Share prompt */}
            <div className="mt-8 pt-6 border-t border-border/50 animate-fade-in-delayed">
              <p className="text-sm text-muted-foreground mb-3 text-center lg:text-left">
                Know someone renting in the Netherlands? Share this tool:
              </p>
              <div className="flex justify-center lg:justify-start">
                <SocialShare compact />
              </div>
            </div>
          </div>

          {/* Right content - Phone mockup */}
          <div
            className="relative flex justify-center animate-slide-in-right"
          >
            <div className="relative">
              {/* Phone frame */}
              <div className="relative w-[280px] md:w-[320px] h-[580px] md:h-[660px] bg-foreground rounded-[3rem] p-3 shadow-2xl">
                <div className="w-full h-full bg-background rounded-[2.5rem] overflow-hidden relative">
                  {/* Notch */}
                  <div className="absolute top-0 left-1/2 -translate-x-1/2 w-32 h-7 bg-foreground rounded-b-2xl z-20" />
                  
                  {/* App content mockup */}
                  <div className="pt-10 px-5 pb-5 h-full flex flex-col">
                    {/* App header */}
                    <div className="flex items-center justify-between mb-6">
                      <div className="flex items-center gap-2">
                        <img 
                          src={logo} 
                          alt="RentSense NL Logo"
                          className="w-8 h-8" 
                          width="32"
                          height="32"
                          fetchPriority="high"
                        />
                        <span className="font-semibold text-foreground text-sm">
                          RentSense NL
                        </span>
                      </div>
                    </div>

                    {/* Clause cards */}
                    <div className="space-y-3 flex-1 overflow-hidden">
                      <ClauseCard
                        status="ok"
                        title="Contract Type"
                        description="Indefinite contract with proper notice period"
                      />
                      <ClauseCard
                        status="risky"
                        title="Rent Increase"
                        description="Annual increase above typical market rate"
                      />
                      <ClauseCard
                        status="problematic"
                        title="Deposit Terms"
                        description="3 months deposit may exceed legal limit"
                      />
                      <ClauseCard
                        status="ok"
                        title="Maintenance"
                        description="Standard responsibility division"
                      />
                    </div>

                    {/* Bottom summary */}
                    <div className="mt-4 p-4 rounded-xl bg-secondary/50">
                      <p className="text-xs text-muted-foreground text-center">
                        2 clauses need your attention
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Floating elements - using CSS animations to avoid JS forced reflows */}
              <div className="absolute -left-16 top-32 p-4 rounded-xl bg-card shadow-card border border-border animate-float">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-status-ok-bg flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-status-ok" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">4 OK</p>
                    <p className="text-xs text-muted-foreground">Clauses</p>
                  </div>
                </div>
              </div>

              <div className="absolute -right-12 bottom-40 p-4 rounded-xl bg-card shadow-card border border-border animate-float-delayed">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-status-risky-bg flex items-center justify-center">
                    <span className="text-status-risky font-bold">!</span>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">2 Flagged</p>
                    <p className="text-xs text-muted-foreground">Need review</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

interface ClauseCardProps {
  status: "ok" | "risky" | "problematic";
  title: string;
  description: string;
}

const ClauseCard = ({ status, title, description }: ClauseCardProps) => {
  const statusConfig = {
    ok: {
      icon: "✓",
      bg: "bg-status-ok-bg",
      text: "text-status-ok",
      border: "border-status-ok/20",
    },
    risky: {
      icon: "!",
      bg: "bg-status-risky-bg",
      text: "text-status-risky",
      border: "border-status-risky/20",
    },
    problematic: {
      icon: "✕",
      bg: "bg-status-problematic-bg",
      text: "text-status-problematic",
      border: "border-status-problematic/20",
    },
  };

  const config = statusConfig[status];

  return (
    <div
      className={`p-3 rounded-xl border ${config.border} ${config.bg} transition-all duration-200`}
    >
      <div className="flex items-start gap-3">
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center ${config.text} font-bold text-xs flex-shrink-0`}
        >
          {config.icon}
        </div>
        <div className="min-w-0">
          <p className="font-medium text-foreground text-sm">{title}</p>
          <p className="text-xs text-muted-foreground line-clamp-2">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
};

export default Hero;
